/**
* @module n2es6/n2mapModule/N2CustomPointStyle
*/

import RegularShape from 'ol/style/RegularShape'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import {asString as ol_color_asString} from 'ol/color'
import N2StackingHistory from './N2StackingHistory'

const colors =
{	
	"classic":	["#ffa500","blue","red","green","cyan","magenta","yellow","#0f0"],
	"dark":		["#960","#003","#900","#060","#099","#909","#990","#090"],
	"pale":		["#fd0","#369","#f64","#3b7","#880","#b5d","#666"],
	"pastel":	["#fb4","#79c","#f66","#7d7","#acc","#fdd","#ff9","#b9b"], 
	"neon":		["#ff0","#0ff","#0f0","#f0f","#f00","#00f"]
}

/**
 * @classdesc
 * Custom style for features point.
 *
 * @constructor
 * @param {object} options
 * @param {String} options.type Chart type: pie,pie3D, donut or bar
 * @param {number} options.radius Chart radius/size, default 20
 * @param {number} options.rotation Rotation in radians (positive rotation clockwise). Default is 0.
 * @param {bool} options.snapToPixel use integral numbers of pixels, default true
 * @param {olstyleStroke} options.stroke stroke style
 * @param {String|Array<olcolor>} options.colors predefined color set "classic","dark","pale","pastel","neon" / array of color string, default classic
 * @param {number} options.offsetX X offset in px
 * @param {number} options.offsetY Y offset in px
 * @param {number} options.animation step in an animation sequence [0,1]
 * @param {number} options.max maximum value for bar chart
 * @api
 */
class N2CustomPointStyle extends RegularShape{
	constructor(opt_options) {
		var options = $n2.extend({
			radius : 0,
			donutScaleFactor: 1,
			stacking : true,
			map : null,
			data: undefined,
			text : undefined, 
			startupOffset : 10,
			feature: null,
			color : '#000000',
			opacity: 1
		}, opt_options)
		
		if (options.stacking){
			var hist = N2CustomPointStyle.__stackingHistory;
			var previousRadius = options.startupOffset;
			if (hist){
				let ext = options.feature.getGeometry().getExtent();
				var cachedPointInfo = hist.getStackingHistory(ext);
				if( cachedPointInfo 
					&& Array.isArray(cachedPointInfo)
					&& cachedPointInfo.length > 0){
					for(var item of cachedPointInfo) {
						var incre = item.extra.incre;
						if (typeof incre === 'number') {
							previousRadius += incre;
						}
					}
				}
			}

			if (options.data
				&& typeof options.data === 'object'
				&& typeof options.data.duration === 'number'){
				options.radius = previousRadius + options.data.duration;
			} else {
				options.radius = previousRadius;
			}
		} else {
			options.data.forEach(function(d){
				options.radius +=Math.ceil(d.duration-0.4);
			})
		}

		options.radius *= 2* options.donutScaleFactor;

		super ({
				radius: options.radius , 
				fill: new Fill({color: [0,0,0,0]}),
				rotation: options.rotation,
				snapToPixel: options.snapToPixel
			});
		
		if (options.scale){
			this.setScale(options.scale);
		}

		this.stroke_ = options.stroke? new Stroke({
			color: options.stroke,
			width: 2
			}): new Stroke({
				color: "#ffffff",
				width: 2
			});

		this.text_ = options.text || null;
		this.donutratio_ = options.donutRatio || 0.5;
		this.donutScaleFactor = options.donutScaleFactor ;
		this.type_ = options.type;
		this.color_ = options.color;
		this.opacity_ = options.opacity;
		
		this.offset_ = [options.offsetX ? options.offsetX : 0, options.offsetY ? options.offsetY : 0];
		this.animation_ = (typeof(options.animation) == 'number') ? { animate:true, step:options.animation } : this.animation_ = { animate:false, step:1 };
		this.max_ = options.max;
		this.startupOffset_ = options.startupOffset;
		this.data_ = options.data;
		this._stacking = options.stacking;

		if (options.feature){
			this._ext = options.feature.getGeometry().getExtent();
		}

		if (options.colors instanceof Array) {
			this.colors_ = options.colors;

		} else {
			this.colors_ = colors[options.colors];
			if (!this.colors_){
				this.colors_ = colors.classic;
			}
		}

		if (options.stacking && options.map){
			if (! N2CustomPointStyle.__stackingHistory){
				N2CustomPointStyle.__stackingHistory = new N2StackingHistory({
					map: options.map
				});
			}
		}

		if (this.data_){
			this.renderCanvas_();
		} else {
			return;
		}
	}

	/**
	 *	Get data associatied with the chart
	 */
	getData() {
		return this.data_;
	}

	/**
	 * Set data associatied with the chart
	 * 
	 * @param {Array<number>} data
	 */
	setData (data) {
		this.data_ = data;
		this.renderCanvas_();
	}

	/**
	 * Get symbol radius
	 */
	getRadius() {
		return this.radius_;
	}

	/** Set symbol radius
	 *	@param {number} symbol radius
	 *	@param {number} donut ratio
	 */
	setRadius(radius, ratio) {
		this.radius_ = radius;
		this.donutratio_ = ratio || this.donutratio_;
		this.renderCanvas_();
	}

	/** Set animation step 
	 *	@param {false|number} false to stop animation or the step of the animation [0,1]
	 */
	setAnimation (step) {
		if (step===false) {
			if (this.animation_.animate == false) return;
			this.animation_.animate = false;
		} else {
			if (this.animation_.step == step) return;
			this.animation_.animate = true;
			this.animation_.step = step;
		}
		this.renderCanvas_();
	}

	_getPreviousRadius (){
		var hist = N2CustomPointStyle.__stackingHistory;
		var rst = this.startupOffset_;
		if (hist){
			var ext = this._ext;
			var cachedPointInfo = hist.getStackingHistory(ext);
			if( cachedPointInfo 
				&& Array.isArray(cachedPointInfo)
				&& cachedPointInfo.length > 0){
				for(var item of cachedPointInfo) {
					var incre = item.extra.incre;
					if (typeof incre === 'number')
					rst += incre;
				}
			}
		}
		return rst;
	}

	_appendNewRadius (ext, radius){
		var hist = N2CustomPointStyle.__stackingHistory;
		
		if (hist){
			var ext = this._ext;
			var extraInfo = {
				incre: radius
			};
			hist.addStackingHistory(ext, extraInfo);
			
		}
		
	}
	
	//only used for treeRing not used for treeRingB
	_getRings (){
		var rings = [];
		var currentRing = null;

		for(var i=0,e=this.data_.length; i<e; ++i){
			var entry = this.data_[i];
			
			var duration = entry.duration;
			var type = entry.type;
			
			if( duration > 0 ) {
				if( null != currentRing 
					&& currentRing.type !== type ) {
					currentRing = null;
				}

				if( null == currentRing ) {
					currentRing = {
						typeName: type.name
						,type: type
						,duration: 0
					};
					rings.push(currentRing);
				}
				currentRing.duration = currentRing.duration + duration;
			}
		}

		return rings;
	}

	/** @private
	*/
	renderCanvas_ (){	
		var strokeStyle;
		var strokeWidth = 0;
	
		if (this.stroke_) {
			strokeStyle = ol_color_asString(this.stroke_.getColor());
			strokeWidth = this.stroke_.getWidth();
		}
	
		//	no atlas manager is used, create a new canvas
		var canvas = this.getImage();
	
		//	draw the circle on the canvas
		var context = (canvas.getContext('2d'));
		context.clearRect(0, 0, canvas.width, canvas.width);
		context.lineJoin = 'round';
	
		var sum=0;
		var i, c;
		for (i=0; i<this.data_.length; i++)
			sum += this.data_[i];
	
		//	reset transform
		context.setTransform(1, 0, 0, 1, 0, 0);
	
		//	then move to (x, y)
		context.translate(0,0);
	
		var step = this.animation_.animate ? this.animation_.step : 1;
		//	console.log(this.animation_.step)

		//	Draw pie
		switch (this.type_){
			case "treeRingB":{
				c = canvas.width/2;
				context.strokeStyle = strokeStyle;
				context.lineWidth = strokeWidth;
				context.save();
				context.rect ( 0,0,2*c,2*c );
				let currRadius = this._getPreviousRadius();
				let ring = this.data_;
				if ( !ring ){
					throw new Error ('N2CustomPointStyle: data is not provided');
				}
				//Make sure the radius and its increment are Integer value
				let dur = Math.ceil(ring.duration-0.4);
				let effectiveRadiusIncre = Math.floor(
						(1 * Math.sqrt( dur / 60 ) * this.donutScaleFactor * 7.7)
				);
	
				//If radius equals to zero or undefined no drawing;
				if ( !effectiveRadiusIncre ){
					break
				}
				var type = ring.type;
				let region = new Path2D();
				region.arc(c, c, currRadius, 0, 2* Math.PI);
				currRadius += effectiveRadiusIncre;
				region.arc(c, c, currRadius, 2* Math.PI, 0);
				region.closePath();
				
				context.strokeStyle = this.stroke_;
				context.stroke(region, 'evenodd');
				context.fillStyle = this.color_
				context.globalAlpha = this.opacity_;
				context.fill(region, 'evenodd');
				if (this.text_){
					context.font = "15px Comic Sans MS";
					context.fillStyle = "black";
					context.textAlign = "top";
					context.textBaseline= "middle";
					context.globalAlpha = 1.0;
					context.fillText(this.text_ , 
						c + Math.cos(-Math.PI/2)*(currRadius+5),
						c + Math.sin(-Math.PI/2)*(currRadius+5)
					);
				}
				
				context.restore();
				this._appendNewRadius(this._ext,effectiveRadiusIncre);
				break;
			}
		}
	
		//	Set Anchor
		var anchor = this.getAnchor();
		anchor[0] = c - this.offset_[0];
		anchor[1] = c - this.offset_[1];

	}
}

export default N2CustomPointStyle
